import React, { useEffect, useMemo, useState } from 'react'
import SponsorshipItemView from './sponsorship-item-view'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Popover,
  Radio,
  RadioGroup,
  TableContainer,
} from '@material-ui/core'
import { useStyles } from './styles'
import EnhancedTable from 'components/tables/enhanced-table'
import NoSponsorships from './no-sponsorships'
import SearchBar from 'pages/pga-coach/students/search-bar'
import StudentsTablePagination from 'pages/pga-coach/students/students-table-pagination'
import SectionEmptyState from 'components/section-empty-state'
import SyncAltOutlinedIcon from 'components/material-symbol/icons/SyncAlt'
import ArrowDropDownIcon from 'components/material-symbol/icons/ArrowDropDown'
import SwapVertIcon from 'components/material-symbol/icons/SwapVert'
import { useIsTablet } from 'lib/hooks'

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Accepted' },
  { value: 'pending', label: 'Invited' },
  { value: 'expired', label: 'Expired' },
]

const sortByName = (a, b, dir) => {
  a = a.props.endorsement.name.toLowerCase().replace(/\s/g, '')
  b = b.props.endorsement.name.toLowerCase().replace(/\s/g, '')

  if (a > b) return dir === 'desc' ? 1 : -1
  if (a < b) return dir === 'desc' ? -1 : 1
  return 0
}

const sortByStatus = (a, b, dir) => {
  a = a.props.endorsement.status.toLowerCase().replace(/\s/g, '')
  b = b.props.endorsement.status.toLowerCase().replace(/\s/g, '')

  if (a > b) return dir === 'desc' ? 1 : -1
  if (a < b) return dir === 'desc' ? -1 : 1
  return 0
}

const sortByExpiredAt = (a, b, dir) => {
  a = a.props.endorsement.expiredAt
  b = b.props.endorsement.expiredAt

  if (a === null) return 1
  if (b === null) return -1
  if (a < b) return dir === 'desc' ? -1 : 1
  if (a === b) return 0
  if (a > b) return dir === 'desc' ? 1 : -1
  return 0
}

const matchesSearchTerm = (endorsement, term) => {
  const normalizedTerm = term.toLowerCase().trim()
  if (!normalizedTerm) return true

  const searchableFields = [
    endorsement.name,
    endorsement.email,
    endorsement.title,
    endorsement.facility?.name,
  ]

  return searchableFields.some(field =>
    field?.toLowerCase().includes(normalizedTerm),
  )
}

const matchesStatusFilter = (endorsement, statusFilter) => {
  if (statusFilter === 'all') return true
  return endorsement.status === statusFilter
}

const SponsorshipsListView = ({ endorsements, facilities, refetch }) => {
  const classes = useStyles()
  const isTablet = useIsTablet()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [page, setPage] = useState(0)
  const rowsPerPage = 25

  const filteredEndorsements = useMemo(() => {
    return (endorsements || []).filter(
      endorsement =>
        matchesSearchTerm(endorsement, searchTerm) &&
        matchesStatusFilter(endorsement, statusFilter),
    )
  }, [endorsements, searchTerm, statusFilter])

  useEffect(() => {
    setPage(0)
  }, [searchTerm, statusFilter, endorsements])

  if (!endorsements?.length) {
    return <NoSponsorships facilities={facilities} refetch={refetch} />
  }

  const filterMenuOpen = Boolean(filterAnchorEl)
  const hasActiveStatusFilter = statusFilter !== 'all'
  const activeStatusLabel =
    STATUS_FILTER_OPTIONS.find(option => option.value === statusFilter)
      ?.label || 'Filters'

  const filterButtons = (
    <>
      <Button
        variant="outlined"
        color="default"
        className={`${classes.filterButton} ${
          hasActiveStatusFilter ? classes.filterButtonActive : ''
        }`}
        aria-controls="sponsorship-filters-menu"
        aria-haspopup="true"
        aria-expanded={filterMenuOpen}
        aria-label={
          hasActiveStatusFilter
            ? `Sponsorship status filter: ${activeStatusLabel} selected`
            : 'Sponsorship status filter'
        }
        onClick={event => setFilterAnchorEl(event.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
      >
        {hasActiveStatusFilter ? activeStatusLabel : 'Filters'}
      </Button>
      <Popover
        id="sponsorship-filters-menu"
        open={filterMenuOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          className: classes.filterMenuPaper,
          style: filterAnchorEl
            ? { minWidth: filterAnchorEl.clientWidth }
            : undefined,
        }}
      >
        <Box className={classes.filterMenuContent}>
          <FormControl
            component="fieldset"
            fullWidth
            className={classes.filterForm}
          >
            <FormLabel component="legend" className={classes.filterLegend}>
              Status
            </FormLabel>
            <RadioGroup
              aria-label="Status filter"
              name="status-filter"
              className={classes.filterRadioGroup}
              value={statusFilter}
              onChange={event => {
                setStatusFilter(event.target.value)
                setFilterAnchorEl(null)
              }}
            >
              {STATUS_FILTER_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  className={classes.filterRadioLabel}
                  control={<Radio color="primary" size="small" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
      </Popover>
    </>
  )

  const columns = [
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      width: isTablet ? '45%' : '28%',
      mobile: true,
      sortable: true,
      sortComparator: sortByName,
    },
    {
      id: 'expiredAt',
      label: 'Access Expiration Date',
      align: 'left',
      width: isTablet ? '0%' : '20%',
      mobile: false,
      sortable: true,
      sortComparator: sortByExpiredAt,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'facility.name',
      label: 'Facility',
      align: 'left',
      width: isTablet ? '0%' : '24%',
      mobile: false,
      sortable: false,
    },
    {
      id: 'status',
      label: 'Status',
      align: 'left',
      width: isTablet ? '30%' : '14%',
      mobile: true,
      sortable: true,
      sortComparator: sortByStatus,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: isTablet ? '25%' : '10%',
      mobile: true,
      sortable: false,
    },
  ]

  const pageCount =
    Math.ceil((filteredEndorsements.length || 0) / rowsPerPage) || 1

  const rows = filteredEndorsements.map(endorsement => (
    <SponsorshipItemView
      endorsement={endorsement}
      key={endorsement.id}
      refetch={refetch}
    />
  ))

  return (
    <TableContainer className={classes.containerStyles}>
      <Box className={classes.tableShell}>
        <Box className={classes.toolbar}>
          <Box className={classes.searchGroup}>
            <SearchBar
              className={classes.searchField}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder="Search Sponsorships"
              data-testid="sponsorship-list-search"
              style={{ margin: 0 }}
            />
            {filterButtons}
          </Box>
          <Box className={classes.toolbarActions}>
            {filteredEndorsements.length > 0 && (
              <StudentsTablePagination
                page={page}
                pageCount={pageCount}
                rowsPerPage={rowsPerPage}
                totalCount={filteredEndorsements.length}
                onPageChange={setPage}
              />
            )}
          </Box>
        </Box>
        <Box className={classes.tableContent}>
          <EnhancedTable
            rows={rows}
            columns={columns}
            label="Sponsorships"
            numberOfRows={rowsPerPage}
            variant="list"
            rowHeight="56px"
            initialOrderBy="expiredAt"
            initialOrder="asc"
            page={page}
            onPageChange={setPage}
            showHeaderPagination={false}
            showPaginator={false}
            emptyState={() => (
              <Box className={classes.listEmptyStateContent}>
                <SectionEmptyState
                  Icon={SyncAltOutlinedIcon}
                  title="Sponsorships"
                  caption="No matching sponsorships."
                  bordered={false}
                />
              </Box>
            )}
          />
        </Box>
      </Box>
    </TableContainer>
  )
}

export default SponsorshipsListView
